// Payment controller that handles subscription purchases based on platform
// Provides a unified interface for handling payments on different platforms
import { PLAN_TYPE, PRODUCT_TYPE } from '@/constants/store-products';
import { Capacitor } from '@capacitor/core';

type PlanId = 'flerte' | 'desejo' | 'atracao' | 'paixao';
type BillingPeriod = 'monthly' | 'annual';

// Links from the existing SubscriptionPlans component - WEB ONLY
const STRIPE_LINKS: Record<string, string> = {
  'flerte-monthly': 'https://buy.stripe.com/8wM15CaSRg4c6xG288',
  'flerte-annual': 'https://buy.stripe.com/dR629Gd0Z3hqe08aEF',
  'desejo-monthly': 'https://buy.stripe.com/28o4hObWVcS0f4c8wy',
  'desejo-annual': 'https://buy.stripe.com/4gw3dK1ihg4c1dmdQT',
  'atracao-monthly': 'https://buy.stripe.com/fZu14hgiFdnP0ZRd3zgw006',
  'atracao-annual': 'https://buy.stripe.com/eVq9ANgiF83v9wnd3zgw007',
  'paixao-monthly': 'https://buy.stripe.com/14kcOk3qpdW44pyfZ5',
  'paixao-annual': 'https://buy.stripe.com/3cs6pW3qp3hq4pyfZ4'
};

/**
 * Check if the current platform is a native app
 */
export const isNativeApp = (): boolean => {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    console.error("Error checking platform:", e);
    return false;
  }
};

/**
 * Check if the current platform is mobile (iOS or Android)
 */
export const isMobilePlatform = (): boolean => {
  try {
    const platform = Capacitor.getPlatform();
    return platform === 'ios' || platform === 'android';
  } catch (e) {
    console.error("Error checking platform:", e);
    return false;
  }
};

/**
 * Redirects to Stripe checkout using existing links - WEB ONLY
 * @param key - The key for the stripe link (e.g. 'flerte-monthly')
 */
const redirectToWebPayment = (key: string): {success: boolean; error?: string} => {
  const platform = Capacitor.getPlatform();
  
  // COMPLETELY BLOCK on mobile platforms
  if (platform === 'ios' || platform === 'android') {
    console.log(`${platform.toUpperCase()}: Stripe payments completely blocked`);
    return { 
      success: false, 
      error: 'Please use the in-app subscription options' 
    };
  }
  
  const link = STRIPE_LINKS[key];
  
  if (!link) {
    console.error(`No payment link found for ${key}`);
    return { success: false, error: 'Invalid subscription plan' };
  }
  
  // Open in new tab for web platform only
  console.log('WEB: Opening Stripe payment link');
  window.open(link, '_blank');
  return { success: true };
};

/**
 * Handles subscription purchase based on platform
 * @param planId - The plan id to purchase (flerte, desejo, paixao)
 * @param billingPeriod - The billing period (monthly, annual)
 * @returns A promise that resolves to an object with success status
 */
export const handleSubscriptionPurchase = async (
  planId: PlanId, 
  billingPeriod: BillingPeriod
): Promise<{success: boolean; error?: string}> => {
  const platform = Capacitor.getPlatform();
  
  // For mobile platforms, COMPLETELY BLOCK Stripe and direct to RevenueCat
  if (platform === 'ios' || platform === 'android') {
    console.log(`${platform.toUpperCase()}: Redirecting to RevenueCat purchase flow`);
    return { 
      success: false, 
      error: 'Please use the subscription plans within the app for mobile purchases' 
    };
  }
  
  // Proceed with web checkout for web platform only
  const key = `${planId}-${billingPeriod}`;
  return redirectToWebPayment(key);
};

/**
 * Restore purchases for native app users
 */
export const restorePurchases = async (): Promise<{success: boolean; error?: string}> => {
  const platform = Capacitor.getPlatform();
  
  if (platform === 'ios' || platform === 'android') {
    // For mobile apps, this should be handled by RevenueCat in the subscription manager
    return { 
      success: false, 
      error: 'Please use the restore button in the subscription settings' 
    };
  }
  
  // For web, there's no restore functionality needed
  return { 
    success: false, 
    error: 'Restore purchases is not available on web' 
  };
};

/**
 * Check if the user has an active subscription - placeholder
 */
export const hasActiveSubscription = async (): Promise<boolean> => false;
